/**
 * Marketplace Settings — Settings → Marketplace.
 *
 * How the adviser appears to visitors on imavenai.com (the public marketplace):
 * opt-in, headline, languages, countries served, specialties, indicative first
 * consultation, response time, and the terms a visitor agrees to before intake.
 *
 * Reads/writes:  GET/PUT {REACT_APP_SERVER}marketplace/me/listing
 * Vocabularies:  GET     {REACT_APP_SERVER}marketplace/options
 *
 * The server rebuilds the public listing on every save and returns the result plus a
 * checklist (`blocking` / `advisory`) so the adviser sees exactly why they are or are
 * not visible. Nothing here affects the widget or the brain.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import CodePicker from './CodePicker';

// ============================================
// STYLES (dark dashboard palette)
// ============================================

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: rgba(15, 20, 35, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.12);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 18px;
`;

const CardHead = styled.h4`
  margin: 0 0 14px 0;
  font-size: 15px;
  font-weight: 600;
  color: #e0e7ff;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  margin-bottom: 6px;
`;

const Help = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.68);
  margin-top: 6px;
  line-height: 1.5;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  color: #fff;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #2563eb; }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  color: #fff;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  &:focus { border-color: #2563eb; }
`;

const SelectInput = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(15,20,35,0.9);
  border: 1px solid rgba(255,255,255,0.12);
  color: #fff;
  font-size: 14px;
  outline: none;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  > * { flex: 1 1 140px; }
`;

const Toggle = styled.button`
  width: 48px; height: 26px; border-radius: 13px; border: none; flex: 0 0 48px;
  background: ${p => p.$active ? 'linear-gradient(135deg, #2563eb 0%, #6366f1 100%)' : 'rgba(255,255,255,0.12)'};
  cursor: pointer; position: relative; transition: all 0.2s ease;
  &::after {
    content: ''; position: absolute; top: 3px; left: ${p => p.$active ? '24px' : '3px'};
    width: 20px; height: 20px; border-radius: 50%; background: #fff; transition: all 0.2s ease;
  }
`;

const ToggleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 12px 0;
`;

const Status = styled.div`
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  font-size: 14px;
  border: 1px solid ${p => p.$tone === 'ok' ? 'rgba(34,197,94,0.4)' : p.$tone === 'warn' ? 'rgba(234,179,8,0.4)' : 'rgba(239,68,68,0.4)'};
  background: ${p => p.$tone === 'ok' ? 'rgba(34,197,94,0.08)' : p.$tone === 'warn' ? 'rgba(234,179,8,0.08)' : 'rgba(239,68,68,0.08)'};
  color: #fff;
  strong { display: block; margin-bottom: 4px; }
  ul { margin: 6px 0 0 0; padding-left: 18px; color: rgba(255,255,255,0.8); }
  li { margin: 2px 0; }
`;

const SaveBar = styled.div`
  display: flex; align-items: center; gap: 12px; margin-top: 8px;
`;

const SaveButton = styled.button`
  padding: 10px 18px; border-radius: 8px; border: none; cursor: pointer;
  background: linear-gradient(135deg, #2563eb 0%, #6366f1 100%);
  color: #fff; font-weight: 600; font-size: 14px;
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Msg = styled.span`
  font-size: 13px;
  color: ${p => p.$error ? '#fca5a5' : '#86efac'};
`;

// ---- preview card ----
const Preview = styled.div`
  position: sticky; top: 16px;
  background: #0b1020;
  border: 1px solid rgba(99,102,241,0.25);
  border-radius: 14px;
  padding: 18px;
`;
const PreviewTitle = styled.div`
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em;
  color: rgba(255,255,255,0.5); margin-bottom: 12px;
`;
const Avatar = styled.div`
  width: 56px; height: 56px; border-radius: 50%; overflow: hidden;
  background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center;
  font-weight: 700; color: #fff; font-size: 20px;
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const PName = styled.div` font-weight: 700; color: #fff; font-size: 16px; margin-top: 10px; `;
const PHead = styled.div` color: rgba(255,255,255,0.8); font-size: 13px; margin-top: 4px; line-height: 1.4; `;
const PMeta = styled.div` color: rgba(255,255,255,0.6); font-size: 12px; margin-top: 10px; line-height: 1.6; `;
const Tag = styled.span`
  display: inline-block; font-size: 11px; padding: 3px 8px; border-radius: 999px; margin: 2px 4px 2px 0;
  background: rgba(99,102,241,0.18); color: #c7d2fe;
`;

// ============================================
// COPY
// ============================================

const ISSUE_TEXT = {
  'system-template': 'System brain templates are never listed.',
  'no-account': 'This profile has no login account.',
  'not-approved': 'Your onboarding has not been approved yet — iMaven reviews new advisers before they go live.',
  'no-widget-key': 'Your widget link is missing. Contact support.',
  'not-opted-in': 'Turn on “Show me on the marketplace” below.',
  'no-photo': 'Add a real photo or logo (Settings → Profile) — cards without one get far fewer clicks.',
  'no-headline': 'Write a one-line headline.',
  'no-languages': 'Pick the languages you consult in — visitors filter by language.',
  'no-countries': 'Pick the countries you serve — visitors filter by country.',
  'no-terms': 'Add your engagement terms — the visitor accepts them before intake starts.',
  'short-bio': 'Your “About” text is very short (Settings → Business Info).',
  'unknown-category': 'Your category is not in the current taxonomy (Settings → Business Info).',
};

const EMPTY = {
  listed: false,
  headline: '',
  languages: [],
  countries: [],
  tags: [],
  yearsExperience: '',
  responseTimeHours: '',
  firstConsultation: { free: false, price: '', currency: 'USD', durationMinutes: '' },
  termsText: '',
};

function normalise(s) {
  if (!s) return { ...EMPTY };
  return {
    ...EMPTY,
    ...s,
    yearsExperience: s.yearsExperience ?? '',
    responseTimeHours: s.responseTimeHours ?? '',
    languages: s.languages || [],
    countries: s.countries || [],
    tags: s.tags || [],
    firstConsultation: { ...EMPTY.firstConsultation, ...(s.firstConsultation || {}) },
    termsText: s.termsText || '',
    headline: s.headline || '',
  };
}

// ============================================
// COMPONENT
// ============================================

export default function MarketplaceSettings() {
  const apiClient = useAuthenticatedAxios();
  const [options, setOptions] = useState({ languages: [], countries: [], currencies: [] });
  const [form, setForm] = useState({ ...EMPTY });
  const [server, setServer] = useState({ listing: null, status: 'hidden', blocking: [], advisory: [] });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mine, opts] = await Promise.all([
          apiClient.get('/marketplace/me/listing'),
          apiClient.get('/marketplace/options'),
        ]);
        if (cancelled) return;
        setForm(normalise(mine.data?.settings));
        setServer({
          listing: mine.data?.listing || null,
          status: mine.data?.status || 'hidden',
          blocking: mine.data?.blocking || [],
          advisory: mine.data?.advisory || [],
        });
        setOptions({
          languages: opts.data?.languages || [],
          countries: opts.data?.countries || [],
          currencies: opts.data?.currencies || [],
        });
      } catch (e) {
        if (!cancelled) setMsg({ error: true, text: 'Could not load marketplace settings.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient]);

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const setFc = (patch) => setForm(prev => ({ ...prev, firstConsultation: { ...prev.firstConsultation, ...patch } }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.tags.some(x => x.toLowerCase() === t.toLowerCase()) || form.tags.length >= 15) { setTagInput(''); return; }
    set({ tags: [...form.tags, t] });
    setTagInput('');
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = {
        listed: !!form.listed,
        headline: form.headline,
        languages: form.languages,
        countries: form.countries,
        tags: form.tags,
        yearsExperience: form.yearsExperience === '' ? null : Number(form.yearsExperience),
        responseTimeHours: form.responseTimeHours === '' ? null : Number(form.responseTimeHours),
        firstConsultation: {
          free: !!form.firstConsultation.free,
          price: form.firstConsultation.free || form.firstConsultation.price === '' ? null : Number(form.firstConsultation.price),
          currency: form.firstConsultation.currency || null,
          durationMinutes: form.firstConsultation.durationMinutes === '' ? null : Number(form.firstConsultation.durationMinutes),
        },
        termsText: form.termsText,
      };
      const res = await apiClient.put('/marketplace/me/listing', { settings: payload });
      setForm(normalise(res.data?.settings));
      setServer({
        listing: res.data?.listing || null,
        status: res.data?.status || 'hidden',
        blocking: res.data?.blocking || [],
        advisory: res.data?.advisory || [],
      });
      setMsg({ error: false, text: 'Saved. Your public listing has been refreshed.' });
    } catch (e) {
      const details = e?.response?.data?.details;
      setMsg({ error: true, text: details?.length ? details.join(' · ') : 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const tone = server.status === 'listed' ? 'ok' : server.status === 'paused' ? 'warn' : 'bad';
  const statusTitle = server.status === 'listed'
    ? 'You are visible on the iMaven marketplace.'
    : server.status === 'paused'
      ? 'Your listing is paused — visitors cannot find you.'
      : 'Your listing is not live yet.';

  const languageLabel = (c) => options.languages.find(o => o.code === c)?.label || c;
  const countryLabel = (c) => options.countries.find(o => o.code === c)?.label || c;
  const preview = useMemo(() => server.listing || {}, [server.listing]);
  const initials = (preview.displayName || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  if (loading) return <Help>Loading marketplace settings…</Help>;

  return (
    <Grid>
      <div>
        <Status $tone={tone}>
          <strong>{statusTitle}</strong>
          {(server.blocking.length > 0 || server.advisory.length > 0) && (
            <ul>
              {server.blocking.map(code => <li key={code}>{ISSUE_TEXT[code] || code}</li>)}
              {server.advisory.map(code => <li key={code} style={{ opacity: 0.8 }}>{ISSUE_TEXT[code] || code}</li>)}
            </ul>
          )}
        </Status>

        <Card>
          <ToggleRow>
            <div>
              <FieldLabel style={{ marginBottom: 2 }}>Show me on the iMaven marketplace</FieldLabel>
              <Help style={{ marginTop: 0 }}>
                Visitors on imavenai.com describe what they need and see matching advisers. When they choose you,
                they accept your terms and start the free intake in your widget — exactly like a visitor from your own link.
              </Help>
            </div>
            <Toggle type="button" $active={!!form.listed} onClick={() => set({ listed: !form.listed })} aria-label="Toggle marketplace listing" />
          </ToggleRow>
        </Card>

        <Card>
          <CardHead>How visitors find you</CardHead>
          <Help style={{ marginTop: 0, marginBottom: 12 }}>
            A visitor types what they need in their own words. We match it to your area, languages and countries and show
            your card next to other advisers. Everything below is what that card says about you — the more precise, the better
            the matches you get.
          </Help>
          <Field>
            <FieldLabel>Headline</FieldLabel>
            <TextInput
              value={form.headline}
              maxLength={140}
              placeholder="e.g. RCIC helping families and skilled workers move to Canada"
              onChange={e => set({ headline: e.target.value })}
            />
            <Help>
              {form.headline.length}/140 — one line under your name on the card. Say who you help and with what, not your
              firm's history. Good: “RCIC — work permits, Express Entry and family sponsorship, 10+ years”. Weak: “Welcome to our website”.
            </Help>
          </Field>
          <Field>
            <FieldLabel>Languages you consult in</FieldLabel>
            <CodePicker
              options={options.languages}
              value={form.languages}
              onChange={languages => set({ languages })}
              placeholder="Search languages…"
              emptyHint="No languages yet — visitors filter by language."
            />
            <Help>
              Languages you can actually run a consultation in. Visitors who wrote in one of them are matched to you first;
              a visitor who filters by a language you did not list will not see you.
            </Help>
          </Field>
          <Field>
            <FieldLabel>Countries you serve</FieldLabel>
            <CodePicker
              options={options.countries}
              value={form.countries}
              onChange={countries => set({ countries })}
              placeholder="Search countries…"
              quickPicks={0}
              emptyHint="No countries yet — where are you licensed / which countries do your clients deal with?"
            />
            <Help>
              The countries your advice is about: where you are licensed, or where your clients' matters are. An immigration
              consultant licensed in Canada lists Canada even if the clients live abroad. Leave it empty only if your work is
              not tied to any country.
            </Help>
          </Field>
          <Field>
            <FieldLabel>Specialties (up to 15)</FieldLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {form.tags.map(t => (
                <Tag key={t} style={{ cursor: 'pointer' }} onClick={() => set({ tags: form.tags.filter(x => x !== t) })} title="Remove">{t} ✕</Tag>
              ))}
            </div>
            <TextInput
              value={tagInput}
              maxLength={40}
              placeholder="Type a specialty and press Enter (e.g. Express Entry, Spousal sponsorship)"
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            />
            <Help>
              Short labels a client would recognise, one per entry: programs, case types, situations. They are shown under
              “Details” on your card and help the match when the visitor's words are specific. Click a label to remove it.
            </Help>
          </Field>
          <Row>
            <Field>
              <FieldLabel>Years of experience</FieldLabel>
              <TextInput type="number" min="0" max="80" value={form.yearsExperience} onChange={e => set({ yearsExperience: e.target.value })} />
              <Help>Years in this profession. Shown as “N+ years” under Details.</Help>
            </Field>
            <Field>
              <FieldLabel>Typical first reply (hours)</FieldLabel>
              <TextInput type="number" min="0" max="720" value={form.responseTimeHours} onChange={e => set({ responseTimeHours: e.target.value })} />
              <Help>
                How long a new lead usually waits for your first personal reply. Shown as “Replies within N hours”; the free
                intake itself answers instantly. Put a number you keep on a normal week, or leave it empty to hide it.
              </Help>
            </Field>
          </Row>
        </Card>

        <Card>
          <CardHead>First consultation (indicative)</CardHead>
          <Help style={{ marginTop: 0, marginBottom: 12 }}>
            Intake in your widget is always free for the visitor. This is only what you usually charge for a first
            consultation afterwards — shown on your card so visitors can filter by price. Nothing is charged on the marketplace.
          </Help>
          <ToggleRow style={{ paddingTop: 0 }}>
            <div>
              <FieldLabel style={{ marginBottom: 0 }}>First consultation is free</FieldLabel>
              <Help style={{ marginTop: 2 }}>On = your card says “first consultation free”. Off = enter what you usually charge and for how long.</Help>
            </div>
            <Toggle type="button" $active={!!form.firstConsultation.free} onClick={() => setFc({ free: !form.firstConsultation.free })} aria-label="First consultation free" />
          </ToggleRow>
          {!form.firstConsultation.free && (
            <Row>
              <Field>
                <FieldLabel>Price</FieldLabel>
                <TextInput type="number" min="0" step="1" value={form.firstConsultation.price} onChange={e => setFc({ price: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel>Currency</FieldLabel>
                <SelectInput value={form.firstConsultation.currency || 'USD'} onChange={e => setFc({ currency: e.target.value })} style={{ width: '100%' }}>
                  {options.currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
                </SelectInput>
              </Field>
              <Field>
                <FieldLabel>Duration (minutes)</FieldLabel>
                <TextInput type="number" min="0" max="600" value={form.firstConsultation.durationMinutes} onChange={e => setFc({ durationMinutes: e.target.value })} />
              </Field>
            </Row>
          )}
        </Card>

        <Card>
          <CardHead>Your engagement terms</CardHead>
          <Help style={{ marginTop: 0, marginBottom: 10 }}>
            The visitor reads and accepts this text before the intake starts. Keep it plain: what the free intake is,
            what happens next, confidentiality, that no fee is due until you agree one together. Every change creates a
            new version; each acceptance records the version the visitor saw.
            {form.termsVersion ? ` Current version: v${form.termsVersion}.` : ''}
          </Help>
          <Help style={{ marginTop: 0, marginBottom: 10 }}>
            A good text covers, in plain words: the intake is free and is not yet a client relationship · what you do with the
            information and that it stays confidential · that professional advice or representation needs a separate agreement
            with its own fees · which languages you communicate in · that the visitor can stop and ask for deletion. Keep it
            under one screen; nobody reads more before a first chat.
          </Help>
          <TextArea
            value={form.termsText}
            maxLength={20000}
            placeholder="e.g. By continuing you agree to share your situation with <Firm>. The initial intake is free and does not create a client relationship or any fee obligation…"
            onChange={e => set({ termsText: e.target.value })}
          />
        </Card>

        <SaveBar>
          <SaveButton type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save marketplace settings'}</SaveButton>
          {msg && <Msg $error={msg.error}>{msg.text}</Msg>}
        </SaveBar>
      </div>

      <div>
        <Preview>
          <PreviewTitle>Card preview</PreviewTitle>
          <Avatar>{preview.photoUrl ? <img src={preview.photoUrl} alt="" /> : initials}</Avatar>
          <PName>{preview.displayName || 'Your name'}</PName>
          <PHead>{form.headline || <span style={{ opacity: 0.5 }}>Your headline will appear here</span>}</PHead>
          <PMeta>
            {preview.categoryLabel && <div>{preview.categoryLabel}{preview.subcategoryLabels?.length ? ` · ${preview.subcategoryLabels.join(', ')}` : ''}</div>}
            {form.languages.length > 0 && <div>Speaks {form.languages.map(languageLabel).join(', ')}</div>}
            {form.countries.length > 0 && <div>Serves {form.countries.map(countryLabel).join(', ')}</div>}
            {preview.rating?.total > 0
              ? <div>★ {preview.rating.average} ({preview.rating.total} reviews)</div>
              : <div style={{ opacity: 0.6 }}>No public reviews yet</div>}
            {form.responseTimeHours !== '' && <div>Replies within {form.responseTimeHours} h</div>}
            <div>
              Free intake
              {form.firstConsultation.free
                ? ' · first consultation free'
                : (form.firstConsultation.price !== '' ? ` · first consultation ${form.firstConsultation.price} ${form.firstConsultation.currency || 'USD'}` : '')}
            </div>
          </PMeta>
          {form.tags.length > 0 && <div style={{ marginTop: 10 }}>{form.tags.map(t => <Tag key={t}>{t}</Tag>)}</div>}
        </Preview>
      </div>
    </Grid>
  );
}
